import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

interface ImageLightboxProps {
  /** Image URL to show (already resolved for <img src>), or null when closed. */
  src: string | null;
  onClose: () => void;
  /** Header title, e.g. the request reference. */
  title?: string;
}

/**
 * Popup showing one enlarged image on the current page. Close with the ✕, a
 * click outside or Esc.
 *
 * Every image opens in the same fixed-size frame: the photo is fitted inside
 * it uncropped and centred, like a print in a mount, so tall and wide photos
 * get the same-sized window.
 *
 * Rendered into <body> through a portal: pages use an `animate-fadeIn`
 * wrapper whose transform would otherwise trap `position: fixed` inside the
 * content area instead of the whole window.
 */
const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, onClose, title = 'Photo' }) => {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    setState('loading');
  }, [src]);

  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while the viewer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [src, onClose]);

  if (!src) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 1100,
        background: 'rgba(15,23,42,.62)',
        backdropFilter: 'blur(6px)',
        animation: 'fadeIn .18s ease both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white overflow-hidden"
        style={{
          width: 'min(800px, 100%)',
          borderRadius: 20,
          boxShadow: '0 24px 60px rgba(15,23,42,.28)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-g100">
          <div className="min-w-0 flex-1 text-[15px] font-bold text-ink truncate">{title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex items-center justify-center rounded-[10px] bg-g100 hover:bg-g200 transition-colors flex-shrink-0"
            style={{ width: 34, height: 34 }}
          >
            <Icon name="x" size={17} color="var(--g600)" />
          </button>
        </div>

        {/* Fixed-size frame */}
        <div
          className="relative flex items-center justify-center bg-g100"
          // Same height for every photo; shrinks with narrow or short windows.
          style={{ height: 'min(460px, calc(100vh - 150px), 80vw)', padding: 'clamp(12px, 3vw, 24px)' }}
        >
          {state === 'loading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[13px] text-g500">
              <span
                className="w-7 h-7 rounded-full animate-spin"
                style={{ border: '3px solid var(--g200)', borderTopColor: 'var(--brand)' }}
              />
              Loading photo…
            </div>
          )}
          {state === 'error' ? (
            <div className="flex flex-col items-center gap-2 text-center text-[13px] text-g500">
              <Icon name="x-circle" size={26} color="var(--g400)" />
              This photo couldn't be loaded.
            </div>
          ) : (
            <img
              src={src}
              alt={title}
              onLoad={() => setState('ready')}
              onError={() => setState('error')}
              className="block bg-white"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(15,23,42,.18)',
                opacity: state === 'ready' ? 1 : 0,
                transition: 'opacity .2s ease',
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
